import type { ItemInstance } from '@headless-tree/core';
import type { ConfigTreeNode } from '~/settings/components/ConfigurationsPage/types';

/** Row height (64) + gap (4) — must match the list layout for scroll adjustment. */
export const listItemSpacing = 64 + 4;

const itemHeight = 64;
const itemSpacing = listItemSpacing;
const chevronOffset = 32;
const levelIndent = 20;
const baseLeftOffset = 22;

export interface VerticalLineConfig {
  top: number;
  height: number;
  left: number;
  key: string;
}

/**
 * Positions the connector line that runs from an expanded folder's chevron down to its
 * last direct child. Depends on every row being exactly `itemSpacing` tall.
 */
export function calculateVerticalLines(
  items: ItemInstance<ConfigTreeNode>[]
): VerticalLineConfig[] {
  const lines: VerticalLineConfig[] = [];

  for (let parentIndex = 0; parentIndex < items.length; parentIndex++) {
    const parentItem = items[parentIndex];
    const parentLevel = parentItem.getItemMeta().level;

    if (!parentItem.isExpanded() || !parentItem.isFolder()) {
      continue;
    }

    let lastChildIndex = -1;
    for (let childIndex = parentIndex + 1; childIndex < items.length; childIndex++) {
      const childLevel = items[childIndex].getItemMeta().level;

      if (childLevel <= parentLevel) {
        break;
      }

      // If this is a direct child (level + 1), track it
      if (childLevel === parentLevel + 1) {
        lastChildIndex = childIndex;
      }
    }

    if (lastChildIndex === -1) {
      continue;
    }

    const top = parentIndex * itemSpacing + chevronOffset;
    const bottom = lastChildIndex * itemSpacing + itemHeight;

    lines.push({
      top,
      height: bottom - top,
      left: parentLevel * levelIndent + baseLeftOffset,
      key: `line-${parentItem.getId()}`,
    });
  }

  return lines;
}
